import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ShopTaxSettings } from './useShopTaxSettings';
import type { ShopTaxRules } from './useShopTaxRules';
import type { ShopFeeRuleRow } from './useShopFeeRules';
import type { TaxLookupAddress, TaxLookupResult } from '@/lib/parts-financial/taxLookup';

export interface SaveTaxConfigPayload {
  shopId: string;
  settings: Omit<ShopTaxSettings, 'id' | 'shop_id'>;
  rules: Pick<ShopTaxRules, 'categories'>;
  feeRules: Array<Partial<Pick<ShopFeeRuleRow, 'id'>> & Omit<ShopFeeRuleRow, 'id' | 'shop_id'>>;
}

export function useSaveTaxSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: SaveTaxConfigPayload) => {
      // Phase 6B-Hardening Item 3: single atomic RPC replaces the previous
      // 4-step client-side write. PL/pgSQL runs inside one transaction, so
      // a failure on any step rolls back all prior steps — the shop can
      // never end up with deleted-but-not-reinserted fee rules.
      const feeRulesPayload = p.feeRules.map(({ id, ...rest }) => rest);
      const { error } = await supabase.rpc('save_shop_tax_config' as any, {
        p_shop_id: p.shopId,
        p_settings: p.settings as any,
        p_categories: p.rules.categories as any,
        p_fee_rules: feeRulesPayload as any,
      });
      if (error) throw error;
    },
    onSuccess: (_, p) => {
      qc.invalidateQueries({ queryKey: ['shop_tax_settings', p.shopId] });
      qc.invalidateQueries({ queryKey: ['shop_tax_rules', p.shopId] });
      qc.invalidateQueries({ queryKey: ['shop_fee_rules', p.shopId] });
    },
  });
}

export interface PersistSnapshotPayload {
  shopId: string;
  address: TaxLookupAddress;
  result: TaxLookupResult;
  userId?: string | null;
}

export function usePersistTaxSnapshot() {
  return useMutation({
    mutationFn: async (p: PersistSnapshotPayload) => {
      const { error } = await supabase.from('shop_tax_snapshots' as any).insert({
        shop_id: p.shopId,
        address: p.address as any,
        payload: p.result.rawPayload as any,
        source_label: p.result.sourceLabel,
        created_by: p.userId ?? null,
      });
      if (error) throw error;
    },
  });
}
